/* eslint-disable no-underscore-dangle */
import PhotoEntity from "../models/photo-entity"

/** @typedef {import('./photos-service').default} PhotosService */
/** @typedef {import('./photos-storage').default} PhotosStorage */
/** @typedef {import('../models/photo-entity').default} PhotoEntity */

const PHOTOS_PER_REQUEST = 1

export default class PhotosRepository {
  /**
   * Creates an instance of PhotosRepository.
   * @param {Object} settings
   * @param {PhotosService} settings.service
   * @param {PhotosStorage} settings.storage
   * @memberof PhotosRepository
   */
  constructor({ service, storage }) {
    this.service = service
    this.storage = storage
  }

  /**
   * @param {Object} settings
   * @param {Number} settings.photoId
   * @param {Object<number, object>} settings.photos
   * @memberof PhotosRepository
   */
  _insertIntoStorage({ photoId, photos }) {
    const photoEntityMap = {}
    Object.entries(photos).forEach(([key, photo]) => {
      if (photo === null) return
      photoEntityMap[key] = PhotoEntity.fromJson(photo)
    })

    this.storage.insert(photoEntityMap, photoId)
  }

  /**
   * @param {Object} settings
   * @param {Number} settings.photoId
   * @param {String} settings.direction
   * @returns {?PhotoEntity}
   * @memberof PhotosRepository
   */
  loadFromStorage({ photoId, direction }) {
    if (direction === "backward") {
      return this.storage.getPreviousOrNull(photoId)
    }

    if (direction === "forward") {
      return this.storage.getNextOrNull(photoId)
    }

    return null
  }

  /**
   * @param {Object} settings
   * @param {Number} settings.photoId
   * @param {String} settings.direction
   * @returns {Promise<?PhotoEntity>}
   * @memberof PhotosRepository
   */
  async loadFromNetwork({ photoId, direction }) {
    const { photos } = await this.service.get(photoId, {
      backward: direction === "backward" ? PHOTOS_PER_REQUEST : 0,
      forward: direction === "forward" ? PHOTOS_PER_REQUEST : 0,
    })

    this._insertIntoStorage({ photoId, photos })

    return this.loadFromStorage({ photoId, direction })
  }

  /**
   * @param {Number} photoId
   * @returns {void}
   * @memberof PhotosRepository
   */
  async preload(photoId) {
    const hasBackward = this.loadFromStorage({ photoId, direction: "backward" }) !== null
    const hasForward = this.loadFromStorage({ photoId, direction: "forward" }) !== null

    if (hasBackward && hasForward) return

    const { photos } = await this.service.get(photoId, {
      backward: hasBackward ? 0 : PHOTOS_PER_REQUEST,
      forward: hasForward ? 0 : PHOTOS_PER_REQUEST,
    })

    this._insertIntoStorage({ photoId, photos })
  }

  /**
   * Loads and returns a previous or next photo.
   * A photo is loaded from the storage or network.
   * If a photo is not presented returns null.
   *
   * @param {Object} settings
   * @param {Number} settings.photoId
   * @param {String} settings.direction
   * @returns {Promise<?PhotoEntity>}
   * @memberof PhotosRepository
   */
  async load({ photoId, direction }) {
    const requestedPhoto = this.loadFromStorage({ photoId, direction })

    if (requestedPhoto !== null) return requestedPhoto

    return this.loadFromNetwork({ photoId, direction })
  }

  /**
   * @param {PhotoEntity} photo
   * @memberof PhotosRepository
   */
  updatePhotoInStorage(photo) {
    this.storage.update(photo)
  }

  /**
   * @param {Number} photoId
   * @param {Number} rating
   * @returns
   * @memberof PhotosRepository
   */
  vote(photoId, rating) {
    return this.service.vote(photoId, rating)
  }
}
